import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Modal,
  Container,
  CircularProgress,
  IconButton,
  Fade,
  Chip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CollectionsIcon from "@mui/icons-material/Collections";
import { SectionContainer, PageTitle, LoadingContainer, AnimatedBox } from "../Components/StyledComponents";
import { loadGallery } from "../api/gallery";
import Seo from "../Components/Seo";

const imageFallback = "linear-gradient(135deg, #2F3A33 0%, #8A5A3B 100%)";

const ProjectImage = ({ image, alt, sx = {} }) => (
  image?.src ? (
    <Box
      component="img"
      src={image.src}
      alt={image.alt || alt}
      loading="lazy"
      onError={(event) => {
        event.currentTarget.style.display = "none";
      }}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        objectPosition: "center center",
        display: "block",
        backgroundColor: "rgba(255,255,255,0.82)",
        ...sx,
      }}
    />
  ) : (
    <Box sx={{ width: "100%", height: "100%", background: imageFallback }} />
  )
);

const Projecten = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const gallery = await loadGallery();
        setProjects(gallery.projects);
        setError(null);
      } catch (error) {
        console.error("Error fetching project gallery:", error);
        setError("Projecten konden niet worden geladen. Controleer de Azure instellingen en probeer het later opnieuw.");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Laden van projecten...
        </Typography>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
        <Seo
          title="Projecten | Voor en Na Stoffeerwerk | Meester Stoffeerders"
          description="Bekijk voor- en nafoto's van stoffeerprojecten, restauraties en maatwerk meubelstoffering van Meester Stoffeerders in Almere."
          path="/projecten"
        />
        <SectionContainer>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <PageTitle>Onze Projecten</PageTitle>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mt: 3, maxWidth: "760px", mx: "auto" }}
              >
                Bekijk renovaties, maatwerk en stoffeerprojecten met duidelijke voor- en na-foto's waar beschikbaar.
              </Typography>
              <Chip
                label={`${projects.length} projecten`}
                color="secondary"
                sx={{ mt: 2, fontWeight: 700 }}
              />
            </Box>

            {projects.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  Er zijn nog geen projecten toegevoegd aan de Azure galerij.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {projects.map((project) => (
                  <Grid item xs={12} sm={6} md={4} key={project.id || project.title}>
                    <AnimatedBox>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          overflow: "hidden",
                        }}
                        onClick={() => setSelectedProject(project)}
                      >
                        <Box sx={{ position: "relative", aspectRatio: "4 / 3", backgroundColor: "background.paper" }}>
                          {project.hasTransformation ? (
                            <Grid container sx={{ height: "100%" }}>
                              <Grid item xs={6} sx={{ position: "relative", overflow: "hidden" }}>
                                <ProjectImage image={project.before} alt={`${project.title} voor`} />
                                <Chip label="Voor" size="small" sx={{ position: "absolute", left: 12, bottom: 12 }} />
                              </Grid>
                              <Grid item xs={6} sx={{ position: "relative", overflow: "hidden" }}>
                                <ProjectImage image={project.after} alt={`${project.title} na`} />
                                <Chip label="Na" color="secondary" size="small" sx={{ position: "absolute", left: 12, bottom: 12 }} />
                              </Grid>
                            </Grid>
                          ) : (
                            <ProjectImage image={project.cover} alt={project.title} />
                          )}
                          <Chip
                            icon={project.hasTransformation ? <CompareArrowsIcon /> : <CollectionsIcon />}
                            label={project.hasTransformation ? "Voor & na" : "Resultaat"}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              backgroundColor: "rgba(255,255,255,0.9)",
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 800 }}>
                            {project.category || "Project"}
                          </Typography>
                          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: "primary.main" }}>
                            {project.title}
                          </Typography>
                          {project.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {project.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </AnimatedBox>
                  </Grid>
                ))}
              </Grid>
            )}

            <Modal open={Boolean(selectedProject)} onClose={() => setSelectedProject(null)}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "94%", md: "86%" },
                  maxWidth: "1180px",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: 2,
                  p: { xs: 2, md: 3 },
                  maxHeight: "90vh",
                  overflow: "auto",
                }}
              >
                {selectedProject && (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
                      <Box>
                        <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 800 }}>
                          {selectedProject.category || "Project"}
                        </Typography>
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: "primary.main" }}>
                          {selectedProject.title}
                        </Typography>
                      </Box>
                      <IconButton onClick={() => setSelectedProject(null)} aria-label="Sluit project">
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    {selectedProject.hasTransformation && (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[selectedProject.before, selectedProject.after].map((image) => (
                          <Grid item xs={12} md={6} key={image.label}>
                            <Box sx={{ position: "relative", aspectRatio: "4 / 3", overflow: "hidden", borderRadius: 2, backgroundColor: "background.paper" }}>
                              <ProjectImage image={image} alt={image.label} />
                              <Chip
                                label={image.label}
                                color={image.label === "Na" ? "secondary" : "default"}
                                sx={{ position: "absolute", left: 16, bottom: 16, fontWeight: 700 }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {!selectedProject.hasTransformation && selectedProject.cover?.src && (
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            position: "relative",
                            aspectRatio: "4 / 3",
                            overflow: "hidden",
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                          }}
                        >
                          <ProjectImage image={selectedProject.cover} alt={selectedProject.title} />
                          <Chip
                            label={selectedProject.cover.label || "Resultaat"}
                            color="secondary"
                            sx={{ position: "absolute", left: 16, bottom: 16, fontWeight: 700 }}
                          />
                        </Box>
                      </Box>
                    )}

                    {selectedProject.images?.length > 0 && (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        {selectedProject.images.map((image, index) => (
                          <Grid item xs={12} sm={6} md={4} key={`${image.src}-${index}`}>
                            <Box
                              sx={{
                                position: "relative",
                                aspectRatio: "4 / 3",
                                overflow: "hidden",
                                borderRadius: 2,
                                backgroundColor: "background.paper",
                              }}
                            >
                              <ProjectImage image={image} alt={image.alt || `${selectedProject.title} detail ${index + 1}`} />
                              <Chip
                                label={image.label || `Detail ${index + 1}`}
                                sx={{ position: "absolute", left: 16, bottom: 16, fontWeight: 700 }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {selectedProject.description && (
                      <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                        {selectedProject.description}
                      </Typography>
                    )}

                    <Button onClick={() => setSelectedProject(null)} sx={{ mt: 2 }}>
                      Sluiten
                    </Button>
                  </>
                )}
              </Box>
            </Modal>
          </Container>
        </SectionContainer>
      </Box>
    </Fade>
  );
};

export default Projecten;
